import React, { useEffect, useRef, useState } from "react"
import { View, StyleSheet, Animated, Easing, PanResponder } from "react-native"

/**
 * Une liste dont on réordonne les lignes en tirant une poignée.
 *
 * **Pourquoi pas `react-native-draggable-flatlist`** — pourtant déjà dans le
 * projet : c'est une `FlatList`, et cet écran vit DANS le ScrollView du
 * gabarit. Une liste virtualisée imbriquée dans un ScrollView se dispute le
 * geste et perd son défilement. Ici les listes font quatre lignes : la
 * virtualisation ne sert à rien, seul le geste compte.
 *
 * Le geste part **de la poignée uniquement**. Le reste de la ligne garde ses
 * boutons, et le ScrollView garde le glissement vertical — un parent qui fait
 * défiler la page ne doit pas réordonner son programme par accident.
 *
 * Les hauteurs sont mesurées, pas supposées : un titre de chapitre passe à deux
 * lignes selon la largeur, et une hauteur en dur ferait sauter la cible d'un
 * cran sur les titres longs.
 */
/**
 * L'entrée d'une ligne à l'arrivée du programme : elle glisse depuis la gauche
 * en fondu, chacune après la précédente — les chapitres se posent, ils ne
 * s'affichent pas. Sans délai (`null`), la ligne est simplement là.
 */
const RowEntrance = ({ delay, children }) => {
    const v = useRef(new Animated.Value(delay == null ? 1 : 0)).current
    useEffect(() => {
        if (delay == null) return
        Animated.timing(v, {
            toValue: 1,
            duration: 420,
            delay,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
        }).start()
    }, [delay, v])
    return (
        <Animated.View
            style={{
                opacity: v,
                transform: [
                    {
                        translateX: v.interpolate({
                            inputRange: [0, 1],
                            outputRange: [-26, 0],
                        }),
                    },
                ],
            }}
        >
            {children}
        </Animated.View>
    )
}

const StageDragList = ({
    /** Délai (ms) avant l'entrée de la 1ʳᵉ ligne ; les suivantes s'enchaînent. */
    entranceDelay = null,
    items,
    keyExtractor,
    renderItem,
    onReorder,
    disabled,
}) => {
    const heights = useRef([])
    const [dragIndex, setDragIndex] = useState(null)
    const [overIndex, setOverIndex] = useState(null)
    const dy = useRef(new Animated.Value(0)).current

    // Les refs suivent le geste ; l'état ne sert qu'au rendu. Le PanResponder
    // est créé une fois et lit des refs — capturer l'état le figerait au
    // premier rendu.
    const dragRef = useRef(null)
    const overRef = useRef(null)
    const pendingRef = useRef(null)

    const targetFor = (offset) => {
        const from = dragRef.current
        if (from == null) return from
        let target = from
        let travelled = 0
        if (offset > 0) {
            for (let i = from + 1; i < items.length; i += 1) {
                travelled += heights.current[i] ?? 0
                if (offset > travelled - (heights.current[i] ?? 0) / 2)
                    target = i
            }
        } else {
            for (let i = from - 1; i >= 0; i -= 1) {
                travelled += heights.current[i] ?? 0
                if (-offset > travelled - (heights.current[i] ?? 0) / 2)
                    target = i
            }
        }
        return target
    }

    // Le responder se crée UNE fois (un `useRef(create())` évaluerait — et
    // jetterait — un responder complet à chaque rendu), et ses gestes lisent
    // `items` / `onReorder` à travers des refs : sans ça, il garderait la
    // fermeture du premier rendu, et un glisser après un rechargement du
    // programme réordonnerait des lignes périmées.
    const latest = useRef({ targetFor, onReorder })
    latest.current = { targetFor, onReorder }
    const responder = useRef(null)
    if (responder.current === null) {
        responder.current = PanResponder.create({
            onStartShouldSetPanResponder: () => pendingRef.current != null,
            onMoveShouldSetPanResponder: () => pendingRef.current != null,
            onPanResponderGrant: () => {
                const index = pendingRef.current
                if (index == null) return
                dragRef.current = index
                overRef.current = index
                setDragIndex(index)
                setOverIndex(index)
                dy.setValue(0)
            },
            onPanResponderMove: (_, gesture) => {
                dy.setValue(gesture.dy)
                const next = latest.current.targetFor(gesture.dy)
                if (next !== overRef.current) {
                    overRef.current = next
                    setOverIndex(next)
                }
            },
            onPanResponderRelease: () => {
                const from = dragRef.current
                const to = overRef.current
                dragRef.current = null
                overRef.current = null
                setDragIndex(null)
                setOverIndex(null)
                dy.setValue(0)
                if (from != null && to != null && from !== to)
                    latest.current.onReorder(from, to)
            },
            onPanResponderTerminate: () => {
                dragRef.current = null
                overRef.current = null
                setDragIndex(null)
                setOverIndex(null)
                dy.setValue(0)
            },
        })
    }

    // Les lignes entre l'origine et la cible cèdent la place, de la hauteur de
    // la ligne tirée : le trou se voit avant le lâcher.
    const shiftFor = (index) => {
        if (dragIndex == null || overIndex == null || index === dragIndex)
            return 0
        const h = heights.current[dragIndex] ?? 0
        if (dragIndex < overIndex && index > dragIndex && index <= overIndex)
            return -h
        if (dragIndex > overIndex && index < dragIndex && index >= overIndex)
            return h
        return 0
    }

    return (
        <View>
            {items.map((item, index) => {
                const dragging = dragIndex === index
                return (
                    <Animated.View
                        key={keyExtractor(item, index)}
                        onLayout={(e) => {
                            heights.current[index] = e.nativeEvent.layout.height
                        }}
                        style={[
                            dragging ? styles.dragging : null,
                            {
                                transform: [
                                    {
                                        translateY: dragging
                                            ? dy
                                            : shiftFor(index),
                                    },
                                ],
                                zIndex: dragging ? 2 : 1,
                            },
                        ]}
                    >
                        <RowEntrance
                            delay={
                                entranceDelay == null
                                    ? null
                                    : entranceDelay + index * 140
                            }
                        >
                            {renderItem(item, index, {
                                dragging,
                                // À poser sur la poignée, et sur elle seule.
                                handleProps: {
                                    ...responder.current.panHandlers,
                                    // Le doigt (ou la souris) s'est posé sur CETTE
                                    // poignée. Le glissement ne démarre qu'au `grant`.
                                    // Les deux événements : `onTouchStart` pour le
                                    // natif, `onPointerDown` pour le web à la souris.
                                    onTouchStart: () => {
                                        pendingRef.current = disabled
                                            ? null
                                            : index
                                    },
                                    onPointerDown: () => {
                                        pendingRef.current = disabled
                                            ? null
                                            : index
                                    },
                                },
                            })}
                        </RowEntrance>
                    </Animated.View>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create({
    dragging: {
        // La ligne tirée décolle : ombre portée + légère opacité, pour qu'on
        // voie qu'elle flotte au-dessus des autres.
        opacity: 0.96,
        shadowColor: "#101d40",
        shadowOpacity: 0.18,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 6 },
        elevation: 6,
    },
})

export default StageDragList
